import {OcrRegion} from './types';

export interface OcrDraftPage {
  baseRevision: number;
  regions: OcrRegion[];
}

export interface OcrDocumentDraft {
  version: 1;
  updatedAt: number;
  pages: Record<string, OcrDraftPage>;
}

const PREFIX = 'lapathoniia-ocr-draft:v1:';
const REGION_TYPES = new Set(['handwritten', 'printed', 'formula', 'table', 'annotation', 'image', 'graph']);

function isDocumentDraft(value: unknown): value is OcrDocumentDraft {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as OcrDocumentDraft;
  if (candidate.version !== 1 || !candidate.pages || typeof candidate.pages !== 'object') return false;
  return Object.values(candidate.pages).every(page => (
    Number.isInteger(page?.baseRevision) && page.baseRevision >= 0 && Array.isArray(page.regions)
    && page.regions.every(region => (
      typeof region?.id === 'string' && Array.isArray(region.bbox) && region.bbox.length === 4
      && region.bbox.every(value => Number.isFinite(value)) && REGION_TYPES.has(region.type)
      && typeof region.text === 'string'
    ))
  ));
}

export function ocrDraftKey(userId: string, sessionId: string, documentId: string): string {
  return `${PREFIX}${userId}:${sessionId}:${documentId}`;
}

export function loadOcrDraft(userId: string, sessionId: string, documentId: string): OcrDocumentDraft | null {
  const key = ocrDraftKey(userId, sessionId, documentId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!isDocumentDraft(value)) {
      localStorage.removeItem(key);
      return null;
    }
    return value;
  } catch {
    try { localStorage.removeItem(key); } catch {}
    return null;
  }
}

export function saveOcrDraft(userId: string, sessionId: string, documentId: string, pages: Record<string, OcrDraftPage>): void {
  localStorage.setItem(ocrDraftKey(userId, sessionId, documentId), JSON.stringify({version: 1, updatedAt: Date.now(), pages}));
}

export function removeOcrDraft(userId: string, sessionId: string, documentId: string): void {
  try { localStorage.removeItem(ocrDraftKey(userId, sessionId, documentId)); } catch {}
}

export function sessionHasOcrDraft(userId: string, sessionId: string): boolean {
  try {
    const prefix = `${PREFIX}${userId}:${sessionId}:`;
    const keys = Array.from({length: localStorage.length}, (_, index) => localStorage.key(index));
    for (const key of keys) {
      if (!key?.startsWith(prefix)) continue;
      const documentId = key.slice(prefix.length);
      if (loadOcrDraft(userId, sessionId, documentId)) return true;
    }
  } catch {}
  return false;
}
